/**
 * React'in üretim yapısındaki hata metinlerini geri açar.
 *
 * Üretim yapısında React, yer kazanmak için hata metinlerini numaraya indirger ve ekrana
 * «Minified React error #185; visit https://react.dev/errors/185 …» yazar. Bu metin hata
 * kaydına olduğu gibi düştüğünde kimse ne olduğunu anlamaz; numarayı elle çevirmek gerekir.
 * Burası o çeviriyi kaydın oluştuğu anda yapar, böylece merkez yönetici ekranında hatanın
 * kendisi görünür.
 *
 * Özgün metinler İngilizce bırakıldı: aranabilir olan ve React belgeleriyle eşleşen karşılık
 * bu. Yanına eklenen günlük dil karşılığı ise kullanıcıya gösterilebilecek olanı.
 */

type ReactHataTanimi = {
  /** React'in sıkıştırılmamış yapıda yazdığı özgün metin. */
  ozgunMetin: string
  /** Eczacıya gösterilebilecek, teknik olmayan karşılık. */
  gunlukDil: string
}

export type CozulmusReactHatasi = ReactHataTanimi & {
  kod: string
  /**
   * React metninden önce gelen kısım, ör. «Uncaught Error:» veya bildirimi yapanın eklediği
   * bağlam. Çeviri sırasında düşerse hatanın nereden geldiği kaybolur.
   */
  onek: string
  /** Hata kaydına yazılacak tek satırlık başlık; önek dâhil değildir. */
  kayitMetni: string
}

/**
 * Yalnızca gerçekten karşılaşılan ve karşılığından emin olunan kodlar var. Listede olmayan bir
 * kod geldiğinde metin bozulmasın diye özgün bağlantı korunur.
 */
const TANIMLAR: Record<string, ReactHataTanimi> = {
  '31': {
    ozgunMetin: 'Objects are not valid as a React child (found: %s).',
    gunlukDil: 'Ekrandaki bir alan, yazı yerine anlamlandıramadığı bir veri aldı.',
  },
  '130': {
    ozgunMetin:
      'Element type is invalid: expected a string (for built-in components) or a class/function (for composite components) but got: %s.',
    gunlukDil: 'Ekranın bir parçası yüklenemedi.',
  },
  '185': {
    ozgunMetin:
      'Maximum update depth exceeded. This can happen when a component repeatedly calls setState inside componentWillUpdate or componentDidUpdate. React limits the number of nested updates to prevent infinite loops.',
    gunlukDil: 'Ekran kendini durmadan yenilemeye çalıştı ve döngüye girdi.',
  },
  '188': {
    ozgunMetin: 'Unable to find node on an unmounted component.',
    gunlukDil: 'Kapanmış bir pencere üzerinde işlem yapılmaya çalışıldı.',
  },
  '300': {
    ozgunMetin:
      'Rendered fewer hooks than expected. This may be caused by an accidental early return statement.',
    gunlukDil: 'Ekran yarım çizildi.',
  },
  '301': {
    ozgunMetin: 'Too many re-renders. React limits the number of renders to prevent an infinite loop.',
    gunlukDil: 'Ekran kendini durmadan yenilemeye çalıştı ve döngüye girdi.',
  },
  '310': {
    ozgunMetin: 'Rendered more hooks than during the previous render.',
    gunlukDil: 'Ekran beklenmedik biçimde değişti ve çizim tamamlanamadı.',
  },
  '321': {
    ozgunMetin:
      'Invalid hook call. Hooks can only be called inside of the body of a function component.',
    gunlukDil: 'Ekranın bir parçası hatalı kurulmuş.',
  },
  '418': {
    ozgunMetin: "Hydration failed because the server rendered HTML didn't match the client.",
    gunlukDil: 'Sayfa sunucudan gelen hâliyle uyuşmadı; yenilemek çoğu zaman yeterli olur.',
  },
  '419': {
    ozgunMetin:
      'The server could not finish this Suspense boundary, likely due to an error during server rendering.',
    gunlukDil: 'Sayfanın bir bölümü sunucuda hazırlanamadı.',
  },
  '421': {
    ozgunMetin: 'This Suspense boundary received an update before it finished hydrating.',
    gunlukDil: 'Sayfa açılırken üzerine yeni bir işlem bindi.',
  },
  '422': {
    ozgunMetin: 'There was an error while hydrating this Suspense boundary.',
    gunlukDil: 'Sayfanın bir bölümü açılırken hata oluştu.',
  },
  '423': {
    ozgunMetin: 'There was an error while hydrating.',
    gunlukDil: 'Sayfa açılırken hata oluştu.',
  },
  '425': {
    ozgunMetin: 'Text content does not match server-rendered HTML.',
    gunlukDil: 'Sayfa sunucudan gelen hâliyle uyuşmadı; yenilemek çoğu zaman yeterli olur.',
  },
  '426': {
    ozgunMetin: 'A component suspended while responding to synchronous input.',
    gunlukDil: 'Ekran, bir işlem tamamlanmadan yenilenmek zorunda kaldı.',
  },
}

const KOD_KALIBI = /Minified React error #(\d+)/
const BAGLANTI_KALIBI = /https:\/\/react\.dev\/errors\/\d+[^\s"')]*/

/**
 * React değişkenleri metne değil bağlantıya koyar (…/errors/130?args[]=undefined). Yerlerine
 * konmazsa mesaj «got: %s» diye eksik kalır ve hangi bileşenin patladığı kaybolur.
 */
function degiskenleriYerlestir(metin: string, baglanti: string | null): string {
  if (!baglanti || !metin.includes('%s')) return metin

  let degiskenler: string[] = []
  try {
    const soruIsareti = baglanti.indexOf('?')
    if (soruIsareti < 0) return metin.replace(/%s/g, '?')
    degiskenler = new URLSearchParams(baglanti.slice(soruIsareti + 1)).getAll('args[]')
  } catch {
    return metin.replace(/%s/g, '?')
  }

  let sira = 0
  return metin.replace(/%s/g, () => {
    const deger = degiskenler[sira]
    sira += 1
    return deger && deger.length > 0 ? deger : '?'
  })
}

/**
 * Sıkıştırılmış bir React hatasıysa açılmış hâlini döndürür, değilse <c>null</c>.
 */
export function reactHatasiniCoz(mesaj: string): CozulmusReactHatasi | null {
  const kodEslesmesi = KOD_KALIBI.exec(mesaj)
  if (!kodEslesmesi) return null

  const kod = kodEslesmesi[1]
  const onek = mesaj.slice(0, kodEslesmesi.index).trim()
  const baglanti = BAGLANTI_KALIBI.exec(mesaj)?.[0] ?? null
  const tanim = TANIMLAR[kod]

  if (!tanim) {
    // Karşılığı bilinmeyen kod: bağlantı korunur ki kayda bakan kişi elle açabilsin.
    const adres = baglanti ?? `https://react.dev/errors/${kod}`
    return {
      kod,
      onek,
      ozgunMetin: `Açıklaması listede yok. Ayrıntı: ${adres}`,
      gunlukDil: 'Ekran beklenmedik bir sorunla karşılaştı.',
      kayitMetni: `React #${kod} (açıklaması listede yok) — ${adres}`,
    }
  }

  const ozgunMetin = degiskenleriYerlestir(tanim.ozgunMetin, baglanti)

  return {
    kod,
    onek,
    ozgunMetin,
    gunlukDil: tanim.gunlukDil,
    kayitMetni: `React #${kod} — ${ozgunMetin}`,
  }
}
